import { ORDER_STATUS } from '../enums/order-status.enum';
import { ORDER_TYPE } from '../enums/order-type.enum';
import { ORDER_SHIP_METHOD } from '../enums/order-ship-method.enum';
import { ORDER_SHIP_CARRIER } from '../enums/order-ship-carrier.enum';
import { OrganizationType } from '../organization/organization.type';
import { UserType } from '../user/user.type';
import { LocationType } from '../location/location.type';
import { EquipmentGroupType } from '../equipment-group/equipment-group.type';
import { OrderFileType } from './order-file/order-file.type';
import { OrderLineType } from './order-line/order-line.type';
import { OrderTrackingInfoType } from './order-tracking-info/order-tracking-info.type';
import { BatteryType } from '../battery/battery.type';
import { DialerType } from '../dialer/dialer.type';
import { EnclosureType } from '../enclosure/enclosure.type';
import { TcSimType } from '../tc-sim/tc-sim.type';
import { OrderItemType } from './order-item/order-item.type';
import { TicketType } from '../ticket/ticket.type';
import { ACSATAType } from '../ata/acs-ata/acs-ata.type';
import { GrandstreamATAType } from '../ata/grandstream-ata/grandstream-ata.type';
import { RouterType } from '../router/router.type';
import { PhoneNumberType } from '../other';
import { DeviceType, ModelType, AttachmentType } from '../inventory/inventory.types';
import { AddressType } from '../../components/Address/Address.types';
import { WarehouseType } from '../warehouse/warehouse.type';
import { ShippingPackageType } from '../inventory/shipping-package.types';
import { parse } from 'date-fns';
import { RMAOrderType } from '../../views/Main/RMAOrders/utils/rmaOrders.types';

export const ORDER_HOLD_REASON_MIN_LENGTH = 3;
export const ORDER_HOLD_REASON_MAX_LENGTH = 200;

export enum DeviceCategory {
  Ata = 'Ata',
  AtaGs = 'AtaGs',
  AtaAcs = 'AtaAcs',
  Battery = 'Battery',
  Dialer = 'Dialer',
  Enclosure = 'Enclosure',
  PowerCycle = 'PowerCycle',
  Router = 'Router',
  Sim = 'Sim',
  Smartphone = 'Smartphone',
}

export enum OrderStatus {
  NewOrder = 'New Order',
  Created = 'Created',
  PendingPayment = 'Pending Payment',
  ApprovedToFulfill = 'Approved to Fulfill',
  AcceptedToFulfill = 'Accepted to Fulfill',
  StagingInProgress = 'Staging in Progress',
  PendingSimActivation = 'Pending SIM Activation',
  PendingSurvey = 'Pending Survey',
  QACheckInProgress = 'QA Check in Progress',
  FailedQACheck = 'Failed QA check',
  KittingInProgress = 'Kitting in Progress',
  PartiallyShipped = 'Partially Shipped',
  Shipped = 'Shipped',
  Delivered = 'Delivered',
  OnHold = 'On Hold',
  Canceled = 'Canceled',
}

export type OrderType = {
  id: number;
  status: ORDER_STATUS;
  order_type: ORDER_TYPE;
  ship_method: ORDER_SHIP_METHOD | null;
  note: string | null;
  ship_carrier: ORDER_SHIP_CARRIER | null;
  tracking_number: string | null;
  so: string | null;
  tracking_numbers: string[] | null;
  order_date: string | null;
  ship_date: string | null;
  created_at: string;
  modified_at: string;

  reseller_organization?: OrganizationType | null;
  reseller_user?: UserType | null;
  fulfillment_rep?: UserType | null;
  organization?: OrganizationType | null;
  location?: LocationType | null;
  equipment_group?: EquipmentGroupType | null;
  ticket?: TicketType | null;
  cc_notify_users?: Array<UserType>;
  attachments?: Array<OrderFileType>;
  order_lines?: Array<OrderLineType>;
  order_tracking_info?: Array<OrderTrackingInfoType>;
  acsAtas?: Array<ACSATAType>;
  atas?: Array<GrandstreamATAType>;
  batteries?: Array<BatteryType>;
  dialers?: Array<DialerType>;
  enclosures?: Array<EnclosureType>;
  routers?: Array<RouterType>;
  tcSims?: Array<TcSimType>;
  order_items?: Array<OrderItemType>;
};

export type VirtualDeviceType = {
  deviceCategory: DeviceCategory;
  id: number;
  model: ModelType;
  vid: string;
  device: DeviceType | null;
  acsAtaPorts?: Array<AcsAtaVirtualPortType>;
};

export type NewOrderType = {
  id: number;
  purchaseEquipment: boolean;
  shippingApprovalRequired: boolean;
  createdAt: string;
  modifiedAt: string;
  hash: string;
  displayName: string;
  status: OrderStatus;
  isTelcobillOrder?: boolean;
  holdReason?: string | null;
  qualityChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  paymentChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  invoiceChecker: Pick<UserType, 'id' | 'firstname' | 'lastname' | 'email'> | null;
  replacementOrder?: Pick<RMAOrderType, 'id' | 'status' | 'createdAt'>;
  rmaOrders?: Array<Pick<RMAOrderType, 'id' | 'status' | 'createdAt'>>;
  invoiceNumber?: string;
  invoiceCreatedDate: string | null;
  invoiceAttachment?: AttachmentType | null;
  paymentNumber?: string;
  organization?: OrganizationType;
  createdBy?: UserType;
  createdByOrgLevel?: number | null;
  resellerUser: { id: number; email: string };
  resellerOrganization?: OrganizationType;
  paymentConfirmedDate: string | null;
  serviceCalls?: {
    installationId: number;
    siteSurveyId: number;
  };
  serviceCallIds: number[];
  requiresDeviceConfirmation?: boolean;
  requiresSimActivation?: boolean;
  orderBundle: OrderBundleType;
  shippingAddress: (AddressType & { id?: number }) | null;
  location: { id: number; displayName: string };
  equipmentGroup: { id: number; displayName: string };
  ccNotifyUsers: Array<UserType>;
  comments: Array<CommentType>;
  virtualDevices: Array<VirtualDeviceType>;
  warehouse: WarehouseType | null;
  shippingPackages: Array<ShippingPackageType>;
  attachments: Array<AttachmentType>;
  lineTypes: Array<OrderTemplateLineType>;
  accessoryBundles?: Array<{
    id: number;
    quantity: number;
    bundle: {
      id: number;
      name?: string;
      displayName?: string;
      items: Array<{
        id: number;
        count: number;
        model: {
          id: number;
          name: string;
          brand?: { name: string };
        };
      }>;
    };
  }>;
};

export type DeviceReportItemType = {
  model: ModelType;
  pendingIn: number;
  pendingOut: number;
  available: number;
};

export type OrderListView = {
  id: number;
  type: 'NEW' | 'OLD';
  status: OrderStatus;
  insufficientEquipment: boolean;
  holdReason?: string | null;
  createdAt: string;
  trackingNumbers: string[];
  shippingDate: string[];
  shippingCarrier: string[];
  resellerOrganization: { id: number; name: string };
  organization: { id: number; name: string };
  warehouse: { id: number; name: string };
  location: { id: number; displayName: string };
  equipmentGroup: { id: number; displayName: string };
  isTelcobillOrder?: boolean;
  orderBundle: OrderBundleType;
  rmaOrder: Pick<RMAOrderType, 'id' | 'priorityReason'>;
};

export type CommentType = {
  id: number;
  displayName: string;
  hash: string;
  createdAt: string;
  modifiedAt: string;
  order: NewOrderType;
  message: string;
  createdBy: UserType;
  isInternal: boolean;
};

export type OrderBundleType = {
  id: number;
  createdAt: string;
  modifiedAt: string;
  hash: string;
  displayName: string;

  ccNotifyUsers: Array<UserType>;
  lineTypes: Array<OrderTemplateLineType>;
  organization: OrganizationType;
  resellerOrganization: OrganizationType;
  resellerUser: { id: number; email: string };
  shippingApprovalRequired: boolean;
  shippingAddress: (AddressType & { id?: number }) | null;
};

export type OrderTemplateLineType = {
  enabled: boolean;
  profile: { id: number; displayName: string } | null;
  system: { id: number; displayName: string } | null;
  provider?: { id: number; displayName: string };
  pbx?: { id: number; displayName: string };
  portNumber?: PhoneNumberType;
  did?: PhoneNumberType;
  pbxExtension?: null | {
    id: number;
    displayName: string;
  };
};

export type OrderTemplateType = {
  createdAt: string;
  description: string;
  displayName: string;
  hash: string;
  id: number;
  modifiedAt: string;
  name: string;
  organization: OrganizationType;
  models: Array<ModelType>;
  lineTypes: Array<OrderTemplateLineType>;
  orderTemplateModels: Array<{
    category: DeviceCategory;
    createdAt: string;
    displayName: string;
    hash: string;
    id: number;
    model: ModelType;
  }>;
};

export type CreateOrderTemplateDto = Pick<OrderTemplateType, 'name' | 'description'> & {
  organization: { id: number };
  models: Array<{
    model: { id: number };
    category: DeviceCategory;
  }>;
  lineTypes: OrderTemplateLineType[];
};

export type CreateOrderCommentDto = {
  order?: { id: number };
  fulfillment?: { id: number };
  rmaOrder?: { id: number };
  message: string;
  isInternal: boolean;
};

export type CreateOrderDto = {
  purchaseEquipment: boolean;
  resellerOrganization: { id: number };
  resellerUser: { id: number };
  ccNotifyUsers: Array<{ id: number; firstname: string; lastname: string; email: string }>;
  equipmentGroups: Array<{ id: number }>;
  models: Array<{
    model: { id: number };
    category: DeviceCategory;
  }>;
  nonSerializedItems?: Array<{
    model: { id: number };
    count: number;
  }>;
  accessoryBundles?: Array<{
    bundle: { id: number };
    quantity: number;
  }>;
  lineTypes: OrderTemplateLineType[];
  shippingApprovalRequired: boolean;
  shippingAddress: null | {
    id?: number;
    address1: string;
    address2?: string | null;
    address3?: string | null;
    name?: string | null;
    state: string;
    country: { id: number };
    city: string;
    lat?: number | null;
    lon?: number | null;
    warning?: string;
    deliverabilityStatus: number;
    zipCode: string;
  };
  organization: { id: number };
  warehouse?: { id: number };
};

export type AcsAtaVirtualDeviceType = {
  acsAtaPorts: Array<AcsAtaVirtualPortType>;
  adminPassword: string | null;
  configOverrides: null | Array<AcsAtaVirtualOverrideType>;
  createdAt: string;
  deviceKey: null | string;
  displayName: string;
  hash: string;
  id: number;
  modifiedAt: string;
  vid: string;
};

export type AcsAtaVirtualPortType = {
  createdAt: string;
  did: null | {
    countryCode: string;
    id: number;
    number: string;
  };
  pbxExtension: null | {
    id: number;
    displayName: string;
  };
  displayName: string;
  enabled: boolean;
  fxsPort: number;
  hash: string;
  id: number;
  modifiedAt: string;
  pbx: null | { id: number; displayName: string };
  portConfigOverrides: null | Array<AcsAtaVirtualOverrideType>;
  profile: null | { id: number; displayName: string };
};

export type AcsAtaVirtualOverrideType = {
  configId: number;
  enabled: boolean;
  value: string;
  name: string;
};

export type ShippingDetailsType = {
  archived: boolean;
  createdAt: string;
  displayName: string;
  hash: string;
  id: number;
  instructions: string;
  isStatusTerminal: boolean;
  lastCheckedAt: null | string;
  modifiedAt: string;
  priority: string;
  shippingDate: string;
  status: null | string;
  trackingNumber: null | string;
  carrier: { id: number; name: string } | null;
  address?: AddressType;
};
